import React, { Suspense, useState, useEffect, lazy } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "./App.css";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Analytics from "./components/Analytics";
import CookieBanner from "./components/CookieBanner";
import useReveal from "./utils/useReveal";

// Code-split routes so first paint stays lean
const Home = lazy(() => import("./pages/Home"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const NotFound = lazy(() => import("./pages/NotFound"));

function ScrollToTop() {
  const { pathname, hash, state } = useLocation();
  useEffect(() => {
    const target = state && state.scrollTo;
    if (target) {
      const tryScroll = (attempt = 0) => {
        const el = document.querySelector(target);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (attempt < 20) {
          requestAnimationFrame(() => tryScroll(attempt + 1));
        }
      };
      tryScroll();
      return;
    }
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash, state]);
  return null;
}

function RouteFallback() {
  return <div style={{ minHeight: "60vh" }} aria-hidden="true" />;
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  useReveal();

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Analytics />
        <div className="app">
          <Header menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
          <main>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
          <CookieBanner />
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
